import useUser from '@vl-core/hooks/useUser';
import { User, UserSchema } from 'vl-common/src/schemas/shared';
import type { RegistrationFormKeys, TRegistrationForm } from '../TRegistrationForm';

export const transformSexFormValue = (value: unknown) => {
  if (typeof value !== 'string' || !['Male', 'Female'].includes(value)) {
    return undefined;
  }
  return String(value).charAt(0);
};

// Extracts the exact keys directly from the Zod schema declaration
type UserKeys = keyof typeof UserSchema.shape;
type ExtraGpKeys = 'gp_first_name' | 'gp_last_name' | 'gp_ods_code' | 'gp_title';

const propertyPairs: Partial<{
  [K in UserKeys | ExtraGpKeys /*NOSONAR*/]: {
    key: RegistrationFormKeys;
    transform?: (value: unknown) => string | number | boolean | undefined;
  };
}> = {
  title: { key: 'title' },
  first_name: { key: 'first_name' },
  last_name: { key: 'last_name' },
  pref_name: { key: 'pref_name' },
  email: { key: 'email' },
  dob: {
    key: 'dob',
    transform: (value: unknown) => {
      const isDateValue = value instanceof Date || (typeof value === 'string' && value !== '');

      return isDateValue ? new Date(value).toISOString().split('T')[0] : undefined;
    }
  },
  tel_mobile_no: { key: 'tel_mobile' },
  tel_other_no: { key: 'tel_other' },
  sex: {
    key: 'sex',
    transform: (value: unknown) => transformSexFormValue(value)
  },
  membership_no: { key: 'mem_no' },
  addline1: { key: 'addline1' },
  addline2: { key: 'addline2' },
  addline3: { key: 'addline3' },
  addline4: { key: 'addline4' },
  region: { key: 'region' },
  postcode: { key: 'postcode' },
  country: { key: 'country' },
  gp_ods_code: { key: 'gp_ods_code' }
};

export const mapUserObjectToFormInitialValues = (user: User): Partial<TRegistrationForm> =>
  Object.fromEntries(
    Object.entries(user).map(([key, value]) => {
      const property = propertyPairs[key as UserKeys | ExtraGpKeys];

      if (property) {
        return [property.key, property.transform ? property.transform(value) : value || undefined];
      }

      return [];
    })
  );

export const useRegistrationFormInitialValues = () => {
  const user = useUser();

  return mapUserObjectToFormInitialValues(user);
};
